import React, { useState, useEffect, useContext } from 'react';
import { usePractice } from '../contexts/PracticeContext';
import { useLocation } from 'react-router-dom';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClearIcon from '@mui/icons-material/Clear';
import AuthContext from '../contexts/AuthContext';

export default function Orders() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens, user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track first load to avoid full-page spinner on incremental searches
  const [initialLoad, setInitialLoad] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [selectedStatus, setSelectedStatus] = useState('all');
  // Initialize searchText from query parameter to highlight a newly created order
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get('search_text') || '';
  const [searchText, setSearchText] = useState(initialSearch);
  const { selectedPractice } = usePractice();

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Apply status filter locally
  const filteredOrders = selectedStatus === 'all'
    ? orders
    : orders.filter(o => o.status === selectedStatus);
  // Sort the filtered orders
  const sortedOrders = filteredOrders.slice().sort((a, b) => {
    let aVal = a[orderBy] ?? '';
    let bVal = b[orderBy] ?? '';
    // compare strings and dates
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    // Wait for authentication
    if (!tokens?.id_token) return;
    // For Personnel (formerly Provider), wait until an organization is selected
    if (user?.role === 'Personnel' && !selectedPractice?.id) return;
    setLoading(true);
    // Build URL with optional practice_id and search_text
    const params = new URLSearchParams();
    if (selectedPractice?.id) params.append('organization_id', selectedPractice.id);
    if (searchText) params.append('search_text', searchText);
    const url = `${apiBase}/orders${params.toString() ? `?${params.toString()}` : ''}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch orders');
        return res.json();
      })
      .then(data => setOrders(data.items || []))
      .catch(err => setError(err.message))
      .finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
  }, [tokens, user, selectedPractice, searchText]);

  // Open populated requisition form in new tab with auth (avoids popup blocking)
  const handleOpenRequisitionForm = async (orderId) => {
    if (!tokens?.id_token) return;
    // Open a blank window synchronously to satisfy browser popup rules
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;
    try {
      const res = await fetch(`${apiBase}/orders/${orderId}/requisition_form`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to download requisition form');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      // Set the location of the new window to the blob URL
      newWindow.location.href = url;
    } catch (err) {
      console.error('Error opening requisition form:', err);
      // Close the window if there was an error
      newWindow.close();
    }
  };

  // Show full-page loader only on initial load
  if (loading && initialLoad) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }
  if (error) {
    return (
      <Container>
        <Typography color="error" variant="h6" sx={{ mt: 4 }}>
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Orders
      </Typography>
      {/* Search and Status filters */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search Orders"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  onClick={() => setSearchText('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={selectedStatus}
            label="Status"
            onChange={e => setSelectedStatus(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            {Array.from(new Set(orders.map(o => o.status))).map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'patient_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'patient_name'}
                  direction={orderBy === 'patient_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('patient_name')}
                >
                  Patient
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'provider_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'provider_name'}
                  direction={orderBy === 'provider_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('provider_name')}
                >
                  Provider
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'practice_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'practice_name'}
                  direction={orderBy === 'practice_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('practice_name')}
                >
                  Practice
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'product_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'product_name'}
                  direction={orderBy === 'product_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('product_name')}
                >
                  Product
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOrders.map(o => {
              const shortId = o.id.split('-')[0];
              return (
                <TableRow key={o.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={o.id} arrow>
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(o.id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{o.patient_name}</TableCell>
                  <TableCell>{o.provider_name}</TableCell>
                  <TableCell>{o.practice_name}</TableCell>
                  <TableCell>{o.product_name}</TableCell>
                  <TableCell>{o.status}</TableCell>
                  <TableCell>{new Date(o.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="open requisition form"
                      onClick={() => handleOpenRequisitionForm(o.id)}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}